import { useState } from "react";
import { api, ApiError } from "../api/client";
import { downloadExport } from "../vault/export";
import type { DecryptedItem } from "../vault/items";
import { useT } from "../i18n";
import { TriangleAlert } from "lucide-react";

// Export confirmation (W5, §20). ExportItems is default-OFF and gated + audited
// server-side: api.exportOrg() records the attempt and returns 403 if not permitted.
// The file is built and downloaded client-side from the already-decrypted items.
export function ExportDialog({
  orgId,
  orgName,
  items,
  onClose,
}: {
  orgId: string;
  orgName: string;
  items: DecryptedItem[];
  onClose: () => void;
}) {
  const t = useT();
  const [fmt, setFmt] = useState<"json" | "csv">("json");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await api.exportOrg(orgId); // gate + audit; throws 403 if not permitted
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      downloadExport(orgName, items, fmt, stamp);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("Export failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50" onClick={onClose}>
      <div className="w-[420px] rounded-md border border-border bg-panel shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-4 font-semibold">{t("Export vault")}</div>
        <div className="flex flex-col gap-4 px-5 py-5">
          <div className="rounded bg-danger/15 px-3 py-2 text-xs text-danger">
            <span className="flex items-start gap-1.5"><TriangleAlert className="h-3.5 w-3.5 shrink-0" /> {t("This file contains all secrets in plaintext. Store it securely and delete it when done. The export is audited.")}</span>
          </div>
          <div className="text-sm text-muted">
            {items.length} {t("items")} · {orgName}
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">{t("Format")}</span>
            <select
              className="rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
              value={fmt}
              onChange={(e) => setFmt(e.target.value as "json" | "csv")}
            >
              <option value="json">{t("JSON (full fidelity)")}</option>
              <option value="csv">{t("CSV (spreadsheet)")}</option>
            </select>
          </label>
          {error && <div className="rounded bg-danger/15 px-2 py-1.5 text-xs text-danger">{error}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded border border-border px-3 py-1.5 text-sm hover:border-accent">
              {t("Cancel")}
            </button>
            <button
              onClick={run}
              disabled={busy}
              className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2 disabled:opacity-60"
            >
              {busy ? "…" : t("Export")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
